/**
 * Sales Orders Filter Configuration
 *
 * Domain-specific filter configuration for URL state management.
 * Defines all filterable fields, their types, defaults, and serialization.
 */

import { createFilterConfig, type FilterFieldConfig } from '@spcc/core'
import type { SalesOrdersFilter, SalesOrderStatus, SalesOrdersSortBy, SalesOrdersSortOrder } from './types'
import { DEFAULT_PAGE_SIZE, DEFAULT_SALES_ORDER_SORT } from './constants'

// ============================================
// Constants
// ============================================

export const SALES_ORDER_STATUS_OPTIONS: Array<{ value: SalesOrderStatus; labelKey: string; color: string }> = [
  { value: 'active', labelKey: 'salesOrders.active', color: 'green' },
  { value: 'completed', labelKey: 'salesOrders.completed', color: 'blue' },
  { value: 'draft', labelKey: 'salesOrders.draft', color: 'gray' },
  { value: 'canceled', labelKey: 'salesOrders.canceled', color: 'red' },
]

export const SALES_ORDER_SORT_FIELDS: Array<{
  field: SalesOrdersSortBy
  labelKey: string
  ascLabelKey: string
  descLabelKey: string
}> = [
  {
    field: 'createdAt',
    labelKey: 'salesOrders.sortByDate',
    ascLabelKey: 'salesOrders.sortOldest',
    descLabelKey: 'salesOrders.sortNewest',
  },
  {
    field: 'salesOrderNumber',
    labelKey: 'salesOrders.sortByOrderNumber',
    ascLabelKey: 'salesOrders.sortByOrderNumberAsc',
    descLabelKey: 'salesOrders.sortByOrderNumberDesc',
  },
  {
    field: 'validityEnd',
    labelKey: 'salesOrders.sortByEndDate',
    ascLabelKey: 'salesOrders.sortByEndDateAsc',
    descLabelKey: 'salesOrders.sortByEndDateDesc',
  },
  {
    field: 'status',
    labelKey: 'salesOrders.sortByStatus',
    ascLabelKey: 'salesOrders.sortByStatusAsc',
    descLabelKey: 'salesOrders.sortByStatusDesc',
  },
]

// ============================================
// Default Filter Values
// ============================================

export const DEFAULT_SALES_ORDERS_FILTER: SalesOrdersFilter = {
  search: undefined,
  status: [],
  type: 'all',
  productCodes: [],
  deliveryAddresses: [],
  factoryIds: [],
  minValue: undefined,
  maxValue: undefined,
  sortBy: DEFAULT_SALES_ORDER_SORT.sortBy as SalesOrdersSortBy,
  sortOrder: DEFAULT_SALES_ORDER_SORT.sortOrder as SalesOrdersSortOrder,
  dateRange: undefined,
  createdDateRange: undefined,
  validityDateRange: undefined,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
}

// ============================================
// Field Configurations
// ============================================

export const SALES_ORDERS_FILTER_FIELDS: FilterFieldConfig[] = [
  {
    id: 'search',
    type: 'text',
    label: 'common.search',
    placeholder: 'salesOrders.searchPlaceholder',
    defaultValue: '',
    debounceMs: 300,
  },
  {
    id: 'status',
    type: 'multi-select',
    label: 'salesOrders.filterByStatus',
    placeholder: 'salesOrders.selectStatus',
    defaultValue: [],
    options: SALES_ORDER_STATUS_OPTIONS.map((opt) => ({
      value: opt.value,
      label: opt.labelKey,
      color: opt.color,
    })),
    showSelectedCount: true,
  },
  {
    id: 'productCodes',
    type: 'multi-select',
    label: 'salesOrders.filterByProduct',
    placeholder: 'salesOrders.selectProduct',
    defaultValue: [],
    searchable: true,
    showSelectedCount: true,
  },
  {
    id: 'deliveryAddresses',
    type: 'multi-select',
    label: 'salesOrders.filterByAddress',
    placeholder: 'salesOrders.selectAddress',
    defaultValue: [],
    searchable: true,
    showSelectedCount: true,
  },
  {
    id: 'factoryIds',
    type: 'multi-select',
    label: 'salesOrders.filterByFactory',
    placeholder: 'salesOrders.selectFactory',
    defaultValue: [],
    searchable: true,
    showSelectedCount: true,
  },
  {
    id: 'valueRange',
    type: 'number-range',
    label: 'salesOrders.filterByValue',
    defaultValue: { min: undefined, max: undefined },
    unit: 'SAR',
    step: 1000,
    formatValue: (v) => v.toLocaleString(),
  },
  {
    id: 'sort',
    type: 'sort',
    label: 'common.sort',
    defaultValue: { field: DEFAULT_SALES_ORDER_SORT.sortBy, order: DEFAULT_SALES_ORDER_SORT.sortOrder },
    sortFields: SALES_ORDER_SORT_FIELDS.map((f) => ({
      field: f.field,
      label: f.labelKey,
      bidirectional: true,
    })),
    excludeFromUrl: false,
  },
]

// ============================================
// Serialization Functions
// ============================================

/**
 * Serialize filter state to URL params
 * Only includes non-default values to keep URLs clean
 */
function serializeSalesOrdersFilter(filter: SalesOrdersFilter): Record<string, string> {
  const params: Record<string, string> = {}

  // Search
  if (filter.search) {
    params.search = filter.search
  }

  // Status (multi-select)
  if (filter.status && filter.status.length > 0) {
    params.status = filter.status.join(',')
  }

  // Product codes (multi-select)
  if (filter.productCodes && filter.productCodes.length > 0) {
    params.products = filter.productCodes.join(',')
  }

  // Delivery addresses (multi-select)
  if (filter.deliveryAddresses && filter.deliveryAddresses.length > 0) {
    params.addresses = filter.deliveryAddresses.join(',')
  }

  // Factory IDs (multi-select)
  if (filter.factoryIds && filter.factoryIds.length > 0) {
    params.factories = filter.factoryIds.join(',')
  }

  // Value range
  if (filter.minValue !== undefined && filter.minValue !== null) {
    params.minValue = String(filter.minValue)
  }
  if (filter.maxValue !== undefined && filter.maxValue !== null) {
    params.maxValue = String(filter.maxValue)
  }

  // Sorting (only if different from default)
  if (filter.sortBy && filter.sortBy !== DEFAULT_SALES_ORDER_SORT.sortBy) {
    params.sortBy = filter.sortBy
  }
  if (filter.sortOrder && filter.sortOrder !== DEFAULT_SALES_ORDER_SORT.sortOrder) {
    params.sortOrder = filter.sortOrder
  }

  // Pagination (only if different from defaults)
  if (filter.page && filter.page > 1) {
    params.page = String(filter.page)
  }
  if (filter.pageSize && filter.pageSize !== DEFAULT_PAGE_SIZE) {
    params.pageSize = String(filter.pageSize)
  }

  // Date range (legacy - kept for backwards compatibility)
  if (filter.dateRange?.from) {
    params.dateFrom = filter.dateRange.from
  }
  if (filter.dateRange?.to) {
    params.dateTo = filter.dateRange.to
  }

  // Created date range
  if (filter.createdDateRange?.from) {
    params.createdFrom = filter.createdDateRange.from
  }
  if (filter.createdDateRange?.to) {
    params.createdTo = filter.createdDateRange.to
  }

  // Validity date range
  if (filter.validityDateRange?.from) {
    params.validityFrom = filter.validityDateRange.from
  }
  if (filter.validityDateRange?.to) {
    params.validityTo = filter.validityDateRange.to
  }

  return params
}

/**
 * Deserialize URL params to partial filter state
 * Returns only fields that are present in URL
 */
function deserializeSalesOrdersFilter(params: Record<string, string | null>): Partial<SalesOrdersFilter> {
  const filter: Partial<SalesOrdersFilter> = {}

  // Search
  if (params.search) {
    filter.search = params.search
  }

  // Status (comma-separated)
  if (params.status) {
    const statuses = params.status.split(',').filter(Boolean) as SalesOrderStatus[]
    if (statuses.length > 0) {
      filter.status = statuses
    }
  }

  // Product codes (comma-separated)
  if (params.products) {
    const codes = params.products.split(',').filter(Boolean)
    if (codes.length > 0) {
      filter.productCodes = codes
    }
  }

  // Delivery addresses (comma-separated)
  if (params.addresses) {
    const addresses = params.addresses.split(',').filter(Boolean)
    if (addresses.length > 0) {
      filter.deliveryAddresses = addresses
    }
  }

  // Factory IDs (comma-separated)
  if (params.factories) {
    const factories = params.factories.split(',').filter(Boolean)
    if (factories.length > 0) {
      filter.factoryIds = factories
    }
  }

  // Value range
  if (params.minValue) {
    const minValue = Number(params.minValue)
    if (Number.isFinite(minValue)) {
      filter.minValue = minValue
    }
  }
  if (params.maxValue) {
    const maxValue = Number(params.maxValue)
    if (Number.isFinite(maxValue)) {
      filter.maxValue = maxValue
    }
  }

  // Sorting
  if (params.sortBy) {
    const sortBy = params.sortBy as SalesOrdersSortBy
    const validSortFields: SalesOrdersSortBy[] = [
      'createdAt',
      'validityEnd',
      'salesOrderNumber',
      'status',
      'totalValue',
      'consumedValue',
      'remainingValue',
      'productCount',
      'ordersCount',
    ]
    if (validSortFields.includes(sortBy)) {
      filter.sortBy = sortBy
    }
  }
  if (params.sortOrder) {
    const sortOrder = params.sortOrder as SalesOrdersSortOrder
    if (sortOrder === 'asc' || sortOrder === 'desc') {
      filter.sortOrder = sortOrder
    }
  }

  // Pagination
  if (params.page) {
    const page = Number(params.page)
    if (Number.isInteger(page) && page > 0) {
      filter.page = page
    }
  }
  if (params.pageSize) {
    const pageSize = Number(params.pageSize)
    if (Number.isInteger(pageSize) && pageSize > 0 && pageSize <= 100) {
      filter.pageSize = pageSize
    }
  }

  // Date range (legacy)
  if (params.dateFrom || params.dateTo) {
    filter.dateRange = {
      from: params.dateFrom || '',
      to: params.dateTo || '',
    }
  }

  // Created date range
  if (params.createdFrom || params.createdTo) {
    filter.createdDateRange = {
      from: params.createdFrom || '',
      to: params.createdTo || '',
    }
  }

  // Validity date range
  if (params.validityFrom || params.validityTo) {
    filter.validityDateRange = {
      from: params.validityFrom || '',
      to: params.validityTo || '',
    }
  }

  return filter
}

/**
 * Validate and sanitize filter values
 */
function validateSalesOrdersFilter(filter: Partial<SalesOrdersFilter>): SalesOrdersFilter {
  const validStatuses: SalesOrderStatus[] = ['active', 'completed', 'draft', 'canceled']
  const validSortFields: SalesOrdersSortBy[] = [
    'createdAt',
    'validityEnd',
    'salesOrderNumber',
    'status',
    'totalValue',
    'consumedValue',
    'remainingValue',
    'productCount',
    'ordersCount',
  ]

  return {
    ...DEFAULT_SALES_ORDERS_FILTER,
    ...filter,
    // Ensure status values are valid
    status: (filter.status || []).filter((s) => validStatuses.includes(s)),
    // Ensure sort field is valid
    sortBy: validSortFields.includes(filter.sortBy as SalesOrdersSortBy)
      ? (filter.sortBy as SalesOrdersSortBy)
      : DEFAULT_SALES_ORDER_SORT.sortBy as SalesOrdersSortBy,
    // Ensure sort order is valid
    sortOrder: filter.sortOrder === 'asc' || filter.sortOrder === 'desc'
      ? filter.sortOrder
      : DEFAULT_SALES_ORDER_SORT.sortOrder as SalesOrdersSortOrder,
    // Ensure page is positive
    page: Math.max(1, filter.page ?? 1),
    // Ensure pageSize is in valid range
    pageSize: Math.min(100, Math.max(1, filter.pageSize ?? DEFAULT_PAGE_SIZE)),
  }
}

// ============================================
// Export Configuration
// ============================================

export const salesOrdersFilterConfig = createFilterConfig<SalesOrdersFilter>({
  domainName: 'salesOrders',
  fields: SALES_ORDERS_FILTER_FIELDS,
  defaults: DEFAULT_SALES_ORDERS_FILTER,
  serialize: serializeSalesOrdersFilter,
  deserialize: deserializeSalesOrdersFilter,
  validate: validateSalesOrdersFilter,
  paramMapping: {
    productCodes: 'products',
    deliveryAddresses: 'addresses',
    factoryIds: 'factories',
  },
})

// ============================================
// URL Search Params Type (for TanStack Router)
// ============================================

/**
 * Type for TanStack Router validateSearch
 */
export interface SalesOrdersSearchParams {
  search?: string
  status?: string
  products?: string
  addresses?: string
  factories?: string
  minValue?: string
  maxValue?: string
  sortBy?: string
  sortOrder?: string
  page?: string
  pageSize?: string
  dateFrom?: string
  dateTo?: string
  createdFrom?: string
  createdTo?: string
  validityFrom?: string
  validityTo?: string
}

/**
 * Validate search params for TanStack Router
 */
export function validateSalesOrdersSearch(search: Record<string, unknown>): SalesOrdersSearchParams {
  return {
    search: typeof search.search === 'string' ? search.search : undefined,
    status: typeof search.status === 'string' ? search.status : undefined,
    products: typeof search.products === 'string' ? search.products : undefined,
    addresses: typeof search.addresses === 'string' ? search.addresses : undefined,
    factories: typeof search.factories === 'string' ? search.factories : undefined,
    minValue: typeof search.minValue === 'string' ? search.minValue : undefined,
    maxValue: typeof search.maxValue === 'string' ? search.maxValue : undefined,
    sortBy: typeof search.sortBy === 'string' ? search.sortBy : undefined,
    sortOrder: typeof search.sortOrder === 'string' ? search.sortOrder : undefined,
    page: typeof search.page === 'string' ? search.page : undefined,
    pageSize: typeof search.pageSize === 'string' ? search.pageSize : undefined,
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    createdFrom: typeof search.createdFrom === 'string' ? search.createdFrom : undefined,
    createdTo: typeof search.createdTo === 'string' ? search.createdTo : undefined,
    validityFrom: typeof search.validityFrom === 'string' ? search.validityFrom : undefined,
    validityTo: typeof search.validityTo === 'string' ? search.validityTo : undefined,
  }
}
